import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { OrganisationClient } from '~/api/clients/types';
import type { Params } from '~/providers/RouterProvider/router.types';

import { useCachedCreditsafeRecord, useClients } from '~/api/clients';
import { Container, Header, Redirect, Title } from '~/components';
import { InvoicesTable } from '~/pages/InvoicesCreditnotes/components/InvoicesTable/InvoicesTable';
import { useInvoicesTotalResults } from '~/pages/InvoicesCreditnotes/components/InvoicesTable/InvoicesTable.hooks';
import { r, routes } from '~/providers/RouterProvider/router.routes';

import { OrganisationActions } from '../components/OrganisationActions';
import {
  OrganisationInfoAddress,
  OrganisationInfoCard,
  OrganisationInfoCreditRating,
  OrganisationInfoGeneral,
  OrganisationInfoPeppol,
  OrganisationRefreshVatInformationButton,
} from '../components/OrganisationInfoCard';
import { isClientRefreshable } from '../utils';
import { pageStyle, sectionStyle, sectionTitleStyle } from './ClientDetail.css';
import { ContactsTable } from './ContactsTable';

export const ClientDetailGuard = () => {
  const { data: clients } = useClients();
  const { clientId } = useParams<Params<typeof routes.showClient>>();

  const client = clients.filter((client) => client.clientType === 'organisation').find((client) => client.id === Number(clientId));

  if (!client) return <Redirect to={routes.clients} />;

  return <ClientDetail client={client} />;
};

type Props = {
  client: OrganisationClient;
};

const ClientDetail = ({ client }: Props) => {
  const cachedCreditsafeRecord = useCachedCreditsafeRecord(client);
  const creditsafeRecord = client.creditsafeRecord ?? cachedCreditsafeRecord;

  const { t } = useTranslation(['clients', 'common']);

  const isBelgianOrganisation = client.addresses.find(({ type }) => type === 'headquarters')?.countryCode === 'BE';
  const contactsCount = useMemo(() => client.contacts.filter(({ deleted }) => !deleted).length, [client.contacts]);
  const invoicesCount = useInvoicesTotalResults({ clientId: client.id });

  return (
    <>
      <Header.Root>
        <Header.Content backTo={routes.clients} backToMobileOnly title={client.name} />

        <Header.Actions>
          <Header.LinkAction icon="Add" to={r(routes.createContact, { clientId: client.id })}>
            {t('clients:detail.contacts.add')}
          </Header.LinkAction>
        </Header.Actions>
      </Header.Root>

      <Container className={pageStyle}>
        <OrganisationInfoCard>
          <OrganisationActions client={client} />

          <OrganisationInfoGeneral
            companyName={client.name}
            otherNumber={client.otherNumber}
            refreshVatInformationButton={isClientRefreshable(client) ? <OrganisationRefreshVatInformationButton client={client} /> : null}
            vatInformationLastUpdatedAt={isClientRefreshable(client) ? client.vatInformationLastUpdatedAt : null}
            vatNumber={client.vatNumber}
          />

          <OrganisationInfoAddress address={client.addresses.find((address) => address.type === 'headquarters') ?? client.addresses[0]} />

          {isBelgianOrganisation && <OrganisationInfoPeppol peppolId={client.peppolId} />}

          <OrganisationInfoCreditRating
            creditsafeRecord={client.creditsafeRecord ?? cachedCreditsafeRecord}
            creditsafeStatus={creditsafeRecord !== null ? 'Success' : client.creditsafeStatus}
          />
        </OrganisationInfoCard>

        <section className={sectionStyle}>
          <Title as="h2" className={sectionTitleStyle} styleAs="h3">
            <Trans i18nKey="clients:detail.contacts.title" values={{ amount: contactsCount }} />
          </Title>

          <div>
            <ContactsTable client={client} />
          </div>
        </section>

        <section className={sectionStyle}>
          <Title as="h2" className={sectionTitleStyle} styleAs="h3">
            {invoicesCount === undefined ? (
              t('clients:detail.invoices.title')
            ) : (
              <Trans i18nKey="clients:detail.invoices.titleWithCount" values={{ amount: invoicesCount }} />
            )}
          </Title>

          <div>
            <InvoicesTable clientId={client.id} />
          </div>
        </section>
      </Container>
    </>
  );
};
